<script setup lang="ts">
/**
 * The smallest possible ask, at the bottom of the configurator.
 *
 * An email address, and the social buttons. That is all this card does now.
 *
 * It used to be the whole identity machine — a three-step wizard ending in a
 * nine-field billing address, rendered inline as the last section of an already
 * long page. Two things were wrong with that, and neither was the form. On a
 * phone `CheckoutMobileBar` is `fixed bottom-0` with the assistant button
 * beside it, so the "create account" button — the most important button in the
 * funnel — rendered underneath both, with the keyboard up. And every sub-step
 * changed the page's height beneath the customer. So the form moved to the
 * commit sheet, which covers the page and has its own scroll and its own
 * footer, and what is left here is the doorway.
 *
 * The point of that doorway being HERE, rather than a wall in front of the
 * checkout, is unchanged: the console used to answer an anonymous visitor's
 * first click with a redirect to `/register`, so the first thing a prospective
 * customer saw was a billing address, before a single price. By this point the
 * price has been on screen the whole way down, and the address is what it
 * actually is — billing details for the invoice we are about to issue.
 *
 * The social buttons stay on the page, not in the sheet, because a social
 * sign-in is one tap and must never require opening a form first. That is the
 * entire conversion argument for offering it.
 */
withDefaults(
  defineProps<{
    /**
     * The configuration is not finished yet.
     *
     * Worth SAYING, and deliberately not worth blocking on. This card used to
     * go `pointer-events-none opacity-50` whenever anything was outstanding —
     * so a customer whose only unmet requirement was the server's access
     * password could not click "Continue with Google". Being refused a sign-in
     * because of a decision about the machine is a non-sequitur, and it was a
     * dead end: the greyed-out card gave no way forward.
     *
     * Identity is independent of the configuration. Signing in early is also
     * strictly better for what follows — it is what loads their subscriptions,
     * private networks and own images. What needs a complete configuration is
     * the ORDER, and that is enforced where the order is placed: reaching a
     * session with something still outstanding lands the customer on the step
     * that wants them, and the review step lists the rest.
     */
    blocked?: boolean
    blockers?: { key: string; label: string }[]
  }>(),
  { blocked: false, blockers: () => [] },
)

const emit = defineEmits<{
  /** Open the commit sheet and answer for this address. */
  (e: 'continue', email: string): void
}>()

const route = useRoute()
/** Where the provider round trip should come back to. */
const returnPath = computed(() => route.fullPath)

const email = ref('')
const emailValid = computed(() => /^\S+@\S+\.\S+$/.test(email.value.trim()))

function submit() {
  if (!emailValid.value) return
  emit('continue', email.value.trim())
}
</script>

<template>
  <div>
    <!-- Say what is missing before asking for anything. Someone who fills in an
         address and is then told to go back and choose an OS has been made to
         do the work in the wrong order. -->
    <CheckoutOrderBlockers
      v-if="blocked"
      id="identity-blockers"
      :blockers="blockers"
      class="mb-4"
    />

    <div class="space-y-4">
      <UFormField :label="$t('page.register.email')" name="identity-email">
        <UInput
          v-model="email"
          type="email"
          name="identity-email"
          autocomplete="email"
          size="xl"
          icon="i-heroicons-envelope"
          :placeholder="$t('page.register.emailDescription')"
          class="w-full"
          @keydown.enter.prevent="submit"
        />
      </UFormField>

      <UButton
        size="lg"
        block
        :disabled="!emailValid"
        :label="$t('billing.page.checkout.identity.continue')"
        @click="submit"
      />

      <!-- The providers come AFTER the address here, so the login page's
           divider ("or continue with email") would point the wrong way. -->
      <div class="flex items-center gap-3 py-1">
        <div class="h-px flex-1 bg-default" />
        <span class="text-xs text-muted">{{ $t('billing.page.checkout.identity.orProvider') }}</span>
        <div class="h-px flex-1 bg-default" />
      </div>

      <!-- One tap, and no form at all. On mobile the provider replaces the tab,
           which is what the saved draft exists for. -->
      <AuthSocialLoginButtons :redirect="returnPath" />
    </div>
  </div>
</template>
